import { CardData, getCardKind, CardKind, isExtraDeckCard } from '../types/cards';
import { asCardFoil } from '../types/foil';
import {
  BattleStep,
  CardInstance,
  CardPosition,
  EnginePhase,
  MatchState,
  MONSTER_ZONE_COUNT,
  otherSeat,
  PlayerZones,
  Seat,
  SPELL_TRAP_ZONE_COUNT,
} from '../types/state';

/**
 * Face-up Ultimate Offering ignition (UDE 2003–04 / Goat, first official
 * FAQ after the vague SDY-041 text): your Main Phase, or the opponent's
 * Battle Phase. Not the Damage Step. Flipping the Continuous Trap itself
 * still follows trap timing (either turn, after the Set delay).
 */
export function canIgniteUltimateOffering(
  turnPlayer: Seat,
  phase: EnginePhase,
  controller: Seat,
  battleStep: BattleStep | null,
): boolean {
  if (battleStep === 'DAMAGE') return false;
  if (turnPlayer === controller) {
    return phase === EnginePhase.MAIN_1 || phase === EnginePhase.MAIN_2;
  }
  return phase === EnginePhase.BATTLE;
}

/** Read-only catalog used by the engine to look up card stats by cardId. */
export type CardLibrary = Map<string, CardData>;

export function buildLibrary(decks: CardData[][]): CardLibrary {
  const lib: CardLibrary = new Map();
  for (const deck of decks) {
    for (const card of deck) {
      const id = String(card.id);
      if (!lib.has(id)) lib.set(id, { ...card, id });
    }
  }
  return lib;
}

export function getCardData(library: CardLibrary, cardId: string): CardData {
  const key = String(cardId);
  const data = library.get(key);
  if (!data) {
    return { id: key, name: `Unknown ${key}`, type: 'Effect Monster' };
  }
  return data;
}

export function makeInstance(
  uid: string,
  cardId: string,
  owner: Seat,
  foil?: string,
): CardInstance {
  return {
    uid,
    cardId: String(cardId),
    owner,
    foil: asCardFoil(foil),
    faceUp: false,
    position: CardPosition.ATTACK,
    summonedThisTurn: false,
    attackedThisTurn: false,
    declaredAttackThisTurn: false,
    positionChangedThisTurn: false,
    setOnTurn: -1,
    statModifiers: [],
    equippedUids: [],
    equippedToUid: null,
  };
}

export function emptyZones(lifePoints: number): PlayerZones {
  return {
    deck: [],
    extraDeck: [],
    hand: [],
    monsters: new Array(MONSTER_ZONE_COUNT).fill(null),
    spellTraps: new Array(SPELL_TRAP_ZONE_COUNT).fill(null),
    fieldSpell: null,
    graveyard: [],
    banished: [],
    lifePoints,
    hasNormalSummoned: false,
    extraNormalSummons: 0,
    lastWillCharges: 0,
    lastWillReady: false,
    lastWillAsk: false,
    soulExchangeUids: [],
    skipBattlePhase: false,
    attackLockTurns: 0,
  };
}

export interface CardLocation {
  seat: Seat;
  zone: keyof PlayerZones;
  index: number; // -1 for non-array zones / not applicable
}

/** Locate where a card uid currently lives. Returns null if not on the field/hand/etc. */
export function findCard(
  state: MatchState,
  uid: string,
): { instance: CardInstance; location: CardLocation } | null {
  for (let seat = 0 as Seat; seat <= 1; seat = (seat + 1) as Seat) {
    const z = state.zones[seat];
    const arrays: { zone: keyof PlayerZones; list: (CardInstance | null)[] }[] = [
      { zone: 'deck', list: z.deck },
      { zone: 'extraDeck', list: z.extraDeck },
      { zone: 'hand', list: z.hand },
      { zone: 'monsters', list: z.monsters },
      { zone: 'spellTraps', list: z.spellTraps },
      { zone: 'graveyard', list: z.graveyard },
      { zone: 'banished', list: z.banished },
    ];
    for (const { zone, list } of arrays) {
      for (let i = 0; i < list.length; i++) {
        if (list[i] && list[i]!.uid === uid) {
          return { instance: list[i]!, location: { seat, zone, index: i } };
        }
      }
    }
    if (z.fieldSpell && z.fieldSpell.uid === uid) {
      return { instance: z.fieldSpell, location: { seat, zone: 'fieldSpell', index: -1 } };
    }
    if (seat === 1) break;
  }
  return null;
}

export function getMonsters(zones: PlayerZones): CardInstance[] {
  return zones.monsters.filter((c): c is CardInstance => c !== null);
}

export function getSpellTraps(zones: PlayerZones): CardInstance[] {
  return zones.spellTraps.filter((c): c is CardInstance => c !== null);
}

export function firstFreeIndex(list: (CardInstance | null)[]): number {
  return list.findIndex((c) => c === null);
}

/**
 * Sum of add/subtract ATK/DEF modifiers. Reverse Trap flips the sign of that
 * sum (LOB-era: +X becomes −X and vice versa; double/half/set-to-value stay
 * out of `statModifiers` and are not inverted).
 */
export function effectiveStatBonus(
  card: CardInstance,
  reverseMods = false,
): { atk: number; def: number } {
  const atk = card.statModifiers.reduce((sum, m) => sum + m.atk, 0);
  const def = card.statModifiers.reduce((sum, m) => sum + m.def, 0);
  return reverseMods ? { atk: -atk, def: -def } : { atk, def };
}

/** Effective ATK/DEF including all stat modifiers. Reverse Trap inverts the mods. */
export function getModifiedAtk(
  card: CardInstance,
  library: CardLibrary,
  reverseMods = false,
): number {
  if (typeof card.atkFinal === 'number') return Math.max(0, card.atkFinal);
  const data = getCardData(library, card.cardId);
  const printedAtk = data.atk ?? 0;
  const printedDef = data.def ?? 0;
  const base = card.baseSwapped ? printedDef : printedAtk;
  return Math.max(0, base + effectiveStatBonus(card, reverseMods).atk);
}

export function getModifiedDef(
  card: CardInstance,
  library: CardLibrary,
  reverseMods = false,
): number {
  const data = getCardData(library, card.cardId);
  const printedAtk = data.atk ?? 0;
  const printedDef = data.def ?? 0;
  const base = card.baseSwapped ? printedAtk : printedDef;
  return Math.max(0, base + effectiveStatBonus(card, reverseMods).def);
}

export function getLevel(card: CardInstance, library: CardLibrary): number {
  return getCardData(library, card.cardId).level ?? 0;
}

export function kindOf(card: CardInstance, library: CardLibrary): CardKind {
  return getCardKind(getCardData(library, card.cardId));
}

/**
 * Flip gate for Ultimate Offering: a Main Deck monster is in hand. Paying
 * 500 LP uses `canNormalOrTributeFromHand` — a monster you cannot summon
 * does not count.
 */
export function handHasNormalSummonMonster(
  zones: PlayerZones,
  library: CardLibrary,
): boolean {
  return zones.hand.some(
    (c) =>
      kindOf(c, library) === CardKind.MONSTER &&
      !isExtraDeckCard(getCardData(library, c.cardId)),
  );
}

/**
 * Opponent monsters this seat may Tribute this turn. Each Soul Exchange
 * activation adds one, and a later activation does not replace the earlier
 * ones. A monster that has left the field no longer counts.
 */
export function soulExchangeTributeUids(state: MatchState, seat: Seat): string[] {
  const marks = state.zones[seat].soulExchangeUids;
  if (!marks || marks.length === 0) return [];
  const opp = new Set(getMonsters(state.zones[otherSeat(seat)]).map((m) => m.uid));
  return marks.filter((uid) => opp.has(uid));
}

/** Tributes required for a normal/tribute summon based on level. */
export function requiredTributes(level: number): number {
  if (level >= 7) return 2;
  if (level >= 5) return 1;
  return 0;
}

/**
 * A Main Deck monster in hand can be Normal Summoned or Set right now,
 * including a Tribute Summon. Ultimate Offering pays 500 LP only then.
 * Tributing your own monster frees a zone. Soul Exchange does not.
 */
export function canNormalOrTributeFromHand(
  state: MatchState,
  seat: Seat,
  library: CardLibrary,
): boolean {
  const zones = state.zones[seat];
  const free = zones.monsters.filter((c) => c === null).length;
  const ownCount = getMonsters(zones).length;
  const soul = soulExchangeTributeUids(state, seat).length;
  const tributePool = ownCount + soul;

  for (const c of zones.hand) {
    if (kindOf(c, library) !== CardKind.MONSTER) continue;
    if (isExtraDeckCard(getCardData(library, c.cardId))) continue;
    const need = requiredTributes(getLevel(c, library));
    if (need === 0) {
      if (free > 0) return true;
      continue;
    }
    if (tributePool < need) continue;
    const ownUsed = Math.min(need, ownCount);
    if (free + ownUsed > 0) return true;
  }
  return false;
}
